import { HttpContextToken, HttpErrorResponse } from '@angular/common/http';
import { MaintenanceConstants } from '../constants/constants';
import { MaintenanceProbeResult } from '../enums/maintenance.enums';

// Set by a caller that acts on a maintenance response itself. The landing probe needs this: it runs
// inside a guard, and a redirect fired from the interceptor would cancel the navigation that guard
// is deciding.
export const SUPPRESS_MAINTENANCE_REDIRECT = new HttpContextToken<boolean>(() => false);

// Lives here rather than on the interceptor so the service can recognise a maintenance response
// without the two importing each other.
export class MaintenanceUtilities {

  // Keyed on the body's code rather than the status: App Service and Front Door also return 503, and
  // theirs carry no code - treating those as maintenance would strand users on the page.
  public static isMaintenanceResponse(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== MaintenanceConstants.MAINTENANCE_STATUS_CODE()) {
      return false;
    }

    return (error.error as { code?: string } | null)?.code === MaintenanceConstants.MAINTENANCE_ERROR_CODE();
  }

  /**
   * Classifies a probe failure. A 401 is the normal answer to an unauthenticated probe and proves the
   * application is serving; an infrastructure failure proves nothing, and treating it as recovery
   * would push a user back into an API that is still restarting mid-deploy.
   */
  public static classifyProbeFailure(error: unknown): MaintenanceProbeResult {
    if (MaintenanceUtilities.isMaintenanceResponse(error)) {
      return MaintenanceProbeResult.Maintenance;
    }

    if (!(error instanceof HttpErrorResponse)) {
      return MaintenanceProbeResult.Unknown;
    }

    // Status 0 is a network or CORS failure, which during a deploy means the API is simply not there
    // yet. The gateway 5xx range is the same story told by Azure rather than by the browser.
    const isInfrastructureFailure = error.status === 0
      || MaintenanceConstants.INFRASTRUCTURE_FAILURE_STATUSES().includes(error.status);

    return isInfrastructureFailure ? MaintenanceProbeResult.Unknown : MaintenanceProbeResult.Available;
  }
}
